import React, { useContext, useState } from "react";
import { useLang, useTextDomainContext, useUser } from "@/app/providers";
import { FaUserEdit } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import {
  getBiddingZoneName,
  useDealsList,
  useDeleteDeal,
  useEnergyType,
  usePriceTypes,
} from "@/hooks";
import { Button, IconButton, Tooltip } from "@mui/material";
import type { GridRenderCellParams } from "@mui/x-data-grid";
import { Link, useNavigate } from "react-router";
import { ConfirmNotification, TableView1 } from "@/components";
import {
  formatDateShort,
  getCountryName,
  removeTimezone,
  toLocaleNumber,
  urlFriendlyString,
} from "@/utils";
import { func, bool } from "prop-types";

type DealsListProps = {
  restricted?: boolean;
};

type DealRow = {
  id: string | number;
  [key: string]: unknown;
};

type DealAction = "edit" | "delete";

type ConfirmResult = {
  result: string;
  parameters?: {
    ids?: Array<string | number>;
  };
};

/**
 *
 * @param props
 */
export const DealsList = (props: DealsListProps) => {
  const { restricted } = props;
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const locale = useLang();
  const navigate = useNavigate();
  const { user } = useUser();

  let { currentTeam: customer, currentSubsidiary: subsidiary } = user || {};
  if (!customer) {
    customer = "all";
  }

  if (!subsidiary) {
    subsidiary = "all";
  }

  const fileNameForExport = urlFriendlyString("energy-contract");

  //region React Hooks (useState)
  // const [building, setBuilding] = useState("all");
  // const [meter, setMeter] = useState("all");
  // const [energyType, setEnergyType] = useState("all");
  // const [fromDate, setFromDate] = useState();
  // const [toDate, setToDate] = useState();
  const [showConfirm, setShowConfirm] = useState(false);
  const [dialogParameters, setDialogParameters] = useState<{
    action?: "delete-single" | "delete-multi";
    ids?: Array<string | number>;
  }>({});
  //endregion  React Hooks (useState)

  const filters = {
    customer,
    subsidiary,
    // building,
    // meter,
    // energyType,
    // fromDate: moment(fromDate).format("YYYY-MM-DDTHH:mm:ss"),
    // toDate: moment(toDate).format("YYYY-MM-DDTHH:mm:ss")
  };
  const { dealsList, loading } = useDealsList(filters);
  const { delete: deleteDeal, deleting } = useDeleteDeal();
  const { energyType: energyTypes } = useEnergyType();
  const priceTypes = usePriceTypes({ countryCode: "all" });

  // useEffect(() => {
  //   setFileNameForExport(urlFriendlyString("energy-contract"));
  // }, []);

  // useEffect(() => {
  //   setFileNameForExport(urlFriendlyString("energy-contracts-" + building + "-" + meter + "-" + energyType + "-" + fromDate + "-" + toDate));
  // }, [building, energyType, fromDate, meter, toDate]);

  // const [disabledDeleteButton, setDisabledDeleteButton] = useState(true);

  // const { dealsList } = useDealsList();
  // const [editableRow, seteditableRow] = useState({});
  // const [edit_show, setedit] = useState(false);
  // const [delete_show, setdelete] = useState(false);
  // const [selectedIds, setSelectedIds] = useState([]);

  // const handleClose_edit = () => setedit(false);

  // const handleShow_edit = () => setedit(true);

  // const handleClose_delete = () => setdelete(false);

  // const handleShow_delete = () => setdelete(true);

  /**
   *
   * @param row
   */
  function edit_functionality(row: DealRow) {
    // seteditableRow(row);
    navigate(`/energy-contract/edit/${row.id}`);
  }

  /**
   *
   * @param row
   */
  function delete_functionality(row: DealRow) {
    setDialogParameters({
      action: "delete-single",
      ids: [row.id],
    });
    setShowConfirm(true);
  }

  // const handleOnRowSelectionModelChange = (ids) => {
  // setSelectedIds(ids);
  // if (ids.length) {
  //   setDisabledDeleteButton(false);
  // } else {
  //   setDisabledDeleteButton(true);
  // }
  // };

  /**
   *
   * @param row
   * @param action
   */
  function performAction(row: DealRow, action: DealAction) {
    switch (action) {
      case "edit":
        edit_functionality(row);
        break;
      case "delete":
        delete_functionality(row);
        break;
      default:
        break;
    }
  }

  /**
   *
   * @param params
   */
  function columnFormatter(params: GridRenderCellParams<DealRow>) {
    return (
      <div className="w-100 d-flex justify-content-center">
        {!restricted && (
          <Tooltip title={gettext("Edit")} arrow>
            <IconButton onClick={() => performAction(params.row, "edit")}>
              <FaUserEdit size={16} />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title={gettext("Delete")} arrow>
          <IconButton onClick={() => performAction(params.row, "delete")}>
            <MdDelete size={16} />
          </IconButton>
        </Tooltip>
      </div>
    );
  }

  const columns = [
    {
      field: "id",
      headerName: gettext("ID"),
      width: 225,
      checkboxSelection: true,
    },
    {
      field: "country",
      headerName: gettext("Country"),
      // flex: 1,
      width: 150,
      headerAlign: "left",
      type: "string",
      valueGetter: (value: string | null | undefined) =>
        getCountryName(value, locale),
    },
    {
      field: "zone",
      headerName: gettext("Zone"),
      // flex: 1,
      width: 180,
      headerAlign: "left",
      type: "string",
      valueGetter: (value: string | null | undefined, row: DealRow) =>
        getBiddingZoneName(value, row?.country as string, gettext),
    },
    {
      field: "energyExchange",
      headerName: gettext("Exchange"),
      // flex: 1,
      width: 300,
      headerAlign: "left",
      type: "string",
    },
    {
      field: "energySupplier",
      headerName: gettext("Supplier"),
      // flex: 1,
      width: 300,
      headerAlign: "left",
      type: "string",
    },
    {
      field: "customer",
      headerName: gettext("Customer"),
      // flex: 1,
      width: 300,
      headerAlign: "left",
      type: "string",
    },
    {
      field: "subsidiary",
      headerName: gettext("Company"),
      // flex: 1,
      width: 300,
      headerAlign: "left",
      type: "string",
    },
    {
      field: "building",
      headerName: gettext("Location"),
      flex: 1,
      minWidth: 100,
      headerAlign: "left",
      type: "string",
      valueGetter: (value: string | null | undefined) =>
        value?.trim().toLowerCase() === "all" ? gettext("All") : (value ?? ""),
    },
    {
      field: "meter",
      headerName: gettext("Installation number"),
      flex: 1,
      minWidth: 100,
      headerAlign: "left",
      type: "string",
      valueGetter: (value: string | null | undefined) =>
        value?.trim().toLowerCase() === "all" ? gettext("All") : (value ?? ""),
    },
    {
      field: "energyType",
      headerName: gettext("Energy Type"),
      // flex: 1,
      width: 150,
      headerAlign: "left",
      type: "string",
      valueGetter: (value: string | null | undefined) => {
        if (!value) return "";
        const trimmed = value.trim();
        if (trimmed.toLowerCase() === "all") {
          return gettext("All");
        }
        const match = energyTypes?.find(
          (item) =>
            item.value?.toString().toLowerCase() === trimmed.toLowerCase(),
        );
        return match?.label || trimmed;
      },
    },
    {
      field: "mainPriceType",
      headerName: gettext("Main Price Type"),
      // flex: 1,
      width: 250,
      headerAlign: "left",
      type: "string",
      valueGetter: (value: string | null | undefined) => {
        if (!value) return "";
        const trimmed = value.trim();
        if (trimmed.toLowerCase() === "all") {
          return gettext("All");
        }
        const match = priceTypes?.find(
          (item) =>
            item.value?.toString().toLowerCase() === trimmed.toLowerCase(),
        );
        return match?.label || trimmed;
      },
    },
    {
      field: "fromDate",
      headerName: gettext("From"),
      headerAlign: "left",
      width: 100,
      type: "date",
      valueGetter: (value: Date | string | null) =>
        value && removeTimezone(value),
      valueFormatter: (value: Date | null) => formatDateShort(value),
    },
    {
      field: "toDate",
      headerName: gettext("To"),
      headerAlign: "left",
      width: 100,
      type: "date",
      valueGetter: (value: Date | string | null) =>
        value && removeTimezone(value),
      valueFormatter: (value: Date | null) => formatDateShort(value),
    },
    {
      field: "currency",
      headerName: gettext("Currency"),
      // flex: 1,
      width: 100,
      headerAlign: "left",
      type: "string",
    },
    {
      field: "priceType",
      headerName: gettext("Price Type"),
      // flex: 1,
      width: 100,
      headerAlign: "left",
      type: "string",
    },
    {
      field: "pricePrice",
      headerName: gettext("Price Price"),
      // flex: 1,
      width: 100,
      headerAlign: "left",
      type: "number",
      valueFormatter: (value: number | string) =>
        toLocaleNumber(locale, value, 2),
    },
    {
      field: "priceKwh",
      headerName: gettext("Price kWh"),
      // flex: 1,
      width: 100,
      headerAlign: "left",
      type: "number",
      valueFormatter: (value: number | string) =>
        toLocaleNumber(locale, value, 2),
    },
    {
      field: "priceShare",
      headerName: gettext("Price Share"),
      // flex: 1,
      width: 100,
      headerAlign: "left",
      type: "number",
      valueFormatter: (value: number | string) =>
        toLocaleNumber(locale, value, 2),
    },
    {
      field: "systemType",
      headerName: gettext("System Type"),
      // flex: 1,
      width: 100,
      headerAlign: "left",
      type: "string",
    },
    {
      field: "systemPrice",
      headerName: gettext("System Price"),
      // flex: 1,
      width: 100,
      headerAlign: "left",
      type: "number",
      valueFormatter: (value: number | string) =>
        toLocaleNumber(locale, value, 2),
    },
    {
      field: "systemKwh",
      headerName: gettext("System kWh"),
      // flex: 1,
      width: 100,
      headerAlign: "left",
      type: "number",
      valueFormatter: (value: number | string) =>
        toLocaleNumber(locale, value, 2),
    },
    {
      field: "systemShare",
      headerName: gettext("System Share"),
      // flex: 1,
      width: 100,
      headerAlign: "left",
      type: "number",
      valueFormatter: (value: number | string) =>
        toLocaleNumber(locale, value, 2),
    },
    {
      field: "epadType",
      headerName: gettext("EPAD Type"),
      // flex: 1,
      width: 100,
      headerAlign: "left",
      type: "string",
    },
    {
      field: "epadPrice",
      headerName: gettext("EPAD Price"),
      // flex: 1,
      width: 100,
      headerAlign: "left",
      type: "number",
      valueFormatter: (value: number | string) =>
        toLocaleNumber(locale, value, 2),
    },
    {
      field: "epadKwh",
      headerName: gettext("EPAD kWh"),
      // flex: 1,
      width: 100,
      headerAlign: "left",
      type: "number",
      valueFormatter: (value: number | string) =>
        toLocaleNumber(locale, value, 2),
    },
    {
      field: "epadShare",
      headerName: gettext("EPAD Share"),
      // flex: 1,
      width: 100,
      headerAlign: "left",
      type: "number",
      valueFormatter: (value: number | string) =>
        toLocaleNumber(locale, value, 2),
    },
    {
      field: "profileType",
      headerName: gettext("Profile Type"),
      // flex: 1,
      width: 100,
      headerAlign: "left",
      type: "string",
    },
    {
      field: "profilePrice",
      headerName: gettext("Profile Price"),
      // flex: 1,
      width: 100,
      headerAlign: "left",
      type: "number",
      valueFormatter: (value: number | string) =>
        toLocaleNumber(locale, value, 2),
    },
    {
      field: "profileKwh",
      headerName: gettext("Profile kWh"),
      // flex: 1,
      width: 100,
      headerAlign: "left",
      type: "number",
      valueFormatter: (value: number | string) =>
        toLocaleNumber(locale, value, 2),
    },
    {
      field: "profileShare",
      headerName: gettext("Profile Share"),
      // flex: 1,
      width: 100,
      headerAlign: "left",
      type: "number",
      valueFormatter: (value: number | string) =>
        toLocaleNumber(locale, value, 2),
    },
    {
      field: "markupPrice",
      headerName: gettext("Markup Price"),
      // flex: 1,
      width: 100,
      headerAlign: "left",
      type: "number",
      valueFormatter: (value: number | string) =>
        toLocaleNumber(locale, value, 2),
    },
    {
      field: "markupShare",
      headerName: gettext("Markup Share"),
      // flex: 1,
      width: 100,
      headerAlign: "left",
      type: "number",
      valueFormatter: (value: number | string) =>
        toLocaleNumber(locale, value, 2),
    },
    {
      field: "commissionPrice",
      headerName: gettext("Spot Premium Price"),
      // flex: 1,
      width: 100,
      headerAlign: "left",
      type: "number",
      valueFormatter: (value: number | string) =>
        toLocaleNumber(locale, value, 2),
    },
    {
      field: "commissionShare",
      headerName: gettext("Spot Premium Share"),
      // flex: 1,
      width: 100,
      headerAlign: "left",
      type: "number",
      valueFormatter: (value: number | string) =>
        toLocaleNumber(locale, value, 2),
    },
    {
      field: "commissionPrice",
      headerName: gettext("Commission Price"),
      // flex: 1,
      width: 100,
      headerAlign: "left",
      type: "number",
    },
    {
      field: "commissionShare",
      headerName: gettext("Commission Share"),
      // flex: 1,
      width: 100,
      headerAlign: "left",
      type: "number",
      valueFormatter: (value: number | string) =>
        toLocaleNumber(locale, value, 2),
    },
    {
      field: "createdAt",
      headerName: gettext("Created At"),
      headerAlign: "left",
      width: 150,
      type: "date",
      valueGetter: (value: string | Date | null) => value && new Date(value),
      valueFormatter: (value: Date | null) => formatDateShort(value),
    },
    {
      field: "updatedAt",
      headerName: gettext("Updated At"),
      headerAlign: "left",
      width: 150,
      type: "date",
      valueGetter: (value: string | Date | null) => value && new Date(value),
      valueFormatter: (value: Date | null) => formatDateShort(value),
    },
    {
      field: "actions",
      headerName: gettext("Actions"),
      hideable: false,
      maxWidth: "100",
      // flex: 1,
      // minWidth: 80,
      // maxWidth: 80,
      // headerAlign: "center",
      // type: "actions",
      renderCell: (params: GridRenderCellParams<DealRow>) =>
        columnFormatter(params),
    },
  ];

  const initialState = {
    pagination: {
      paginationModel: {
        page: 0,
        pageSize: 100,
      },
    },
    columns: {
      columnVisibilityModel: {
        id: false,
        customer: false,
        currency: false,
        energyExchange: false,
        energySupplier: false,
        // mainPriceType: false,
        priceType: false,
        pricePrice: false,
        priceKwh: false,
        priceShare: false,
        systemType: false,
        systemPrice: false,
        systemKwh: false,
        systemShare: false,
        epadType: false,
        epadPrice: false,
        epadKwh: false,
        epadShare: false,
        profileType: false,
        profileKwh: false,
        profilePrice: false,
        profileShare: false,
        markupPrice: false,
        markupShare: false,
        commissionPrice: false,
        commissionShare: false,
        createdAt: false,
        updatedAt: false,
      },
    },
  };

  // const handleGetSelectedValues = (selectedValues) => {
  //   if (selectedValues?.building?.value) {
  //     setBuilding(selectedValues.building.value);
  //   }
  //
  //   if (selectedValues?.meter?.value) {
  //     setMeter(selectedValues.meter.value);
  //   }
  //
  //   if (selectedValues?.energyType?.value) {
  //     setEnergyType(selectedValues.energyType.value);
  //   }
  //
  //   if (selectedValues?.date?.startDate) {
  //     setFromDate(selectedValues.date.startDate.toISOString());
  //   }
  //
  //   if (selectedValues?.date?.endDate) {
  //     setToDate(selectedValues.date.endDate.toISOString());
  //   }
  //
  // };

  const handleOnClickDeleteMultiple = (ids: Array<string | number>) => {
    setDialogParameters({
      action: "delete-multi",
      ids: ids,
    });
    setShowConfirm(true);
  };

  const handleGetResult = (obj: ConfirmResult) => {
    if (obj.result === "yes") {
      deleteDeal({
        ids: (obj?.parameters?.ids || []).map(String),
      });
    }
    setShowConfirm(false);
  };

  const preToolbar = () => {
    return (
      <>
        {/* <Button variant="contained" component={Link} to={"/data/deals/add"}> */}
        {/*   {gettext("Add contract")} */}
        {/* </Button> */}
        {/* <Button disabled={disabledDeleteButton} variant="contained" color={"error"}> */}
        {/*   Delete */}
        {/* </Button> */}
      </>
    );
  };

  const postToolbar = () => {
    return (
      <>
        <Button
          variant="contained"
          component={Link}
          to={"/energy-contract/add"}
          sx={{
            ml: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {gettext("Add contract")}
        </Button>
        {/* <Button disabled={disabledDeleteButton} variant="contained" color={"error"}> */}
        {/*   Delete */}
        {/* </Button> */}
      </>
    );
  };

  const headerRight = () => {
    return (
      <>
        {/* <Button variant="contained" component={Link} to={"/data/deals/add"}> */}
        {/*   {gettext("Add contract")} */}
        {/* </Button> */}
      </>
    );
  };

  return (
    <>
      <TableView1
        title={gettext("Contracts")}
        rows={dealsList}
        columns={columns}
        loading={loading}
        checkboxSelection={false}
        initialState={initialState}
        preToolbar={preToolbar()}
        postToolbar={postToolbar()}
        headerRight={headerRight()}
        showGridToolbar
        showGridToolbarExport={false}
        showGridToolbarDeleteButton={false}
        showGridToolbarQuickFilter
        fileNameForExport={fileNameForExport}
        onClickDeleteMultiple={handleOnClickDeleteMultiple}
      />

      {/* <DataGridComponent */}
      {/*   rows={dealsList} */}
      {/*   columns={columns} */}
      {/*   loading={loading} */}
      {/*   disableSelectionOnClick={true} */}
      {/*   initialState={{ pinnedColumns: { right: ["actions"] } }} */}
      {/*   autoHeight */}
      {/*   // getRowId={row => row.Id} */}
      {/* /> */}

      {/* <AddDealForm */}
      {/*   open={edit_show} */}
      {/*   handleClose={handleClose_edit} */}
      {/*   editableRow={editableRow} */}
      {/* /> */}

      {/* <DeleteModel */}
      {/*   show={delete_show} */}
      {/*   onHide={handleClose_delete} */}
      {/*   editableRow={editableRow} */}
      {/*   // getTeamMembers={props.getTeamMembers} */}
      {/* /> */}
      <ConfirmNotification
        isOpen={showConfirm}
        parameters={dialogParameters}
        getResult={handleGetResult}
      />
      {deleting ? (
        <div className="position-absolute bg-dark opacity-50 w-100 h-100 d-flex justify-content-center align-items-center">
          {/*<Spinner animation="border" variant="light"/>*/}
        </div>
      ) : null}
    </>
  );
};

DealsList.propTypes = {
  gettext: func,
  restricted: bool,
};
